'use client';

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import debounce from 'lodash.debounce';
import uniqBy from 'lodash.uniqby';
import { useInfiniteQuery } from '@tanstack/react-query';

import { Product } from '@/types/product';
import { getProducts } from '@/api/products';
import { useUserDataStore } from '@/stores/userDataStore';

export const parseFetchedProduct = (product) => {
  const formattedImages = product.images?.map((image) => ({
    ...image,
    src: image.url,
  }));

  return {
    ...product,
    images: formattedImages,
  };
};

const useProductSelect = () => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Product[]>([]);

  const { appUrl } = useUserDataStore(
    useShallow((state) => ({
      appUrl: state.appUrl,
    }))
  );

  const transformProductResponse = (response) => {
    if (!response) return { searchProducts: [], total: 0, nextPageFrom: 0 };

    return {
      searchProducts: (response.items || []).map((product) =>
        parseFetchedProduct({
          ...product,
          productId: product._id,
          id: product._id,
          name: product.title,
          images: product.images || [],
        })
      ),
      total: response.total || 0,
      nextPageFrom: response.nextPage || 0,
    };
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error, refetch } = useInfiniteQuery(
    {
      queryKey: ['products', appUrl, query],
      queryFn: async ({ pageParam = 0 }) => {
        const response = await getProducts({
          appUrl,
          searchTerm: query,
          size: 20,
          from: pageParam,
        });
        return transformProductResponse(response);
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage.nextPageFrom && lastPage.nextPageFrom < lastPage.total) {
          return lastPage.nextPageFrom;
        }
        return undefined;
      },
      enabled: !!appUrl,
    }
  );

  useEffect(() => {
    if (data) {
      const allProducts = data.pages.flatMap((page) => page.searchProducts);
      setOptions(uniqBy(allProducts, 'id'));
    }
  }, [data]);

  const debouncedSetQuery = useRef(
    debounce((value: string) => {
      setQuery(value);
    }, 300)
  ).current;

  const handleSearchInputChange = useCallback(
    (searchTerm: string) => {
      setInputValue(searchTerm);
      debouncedSetQuery(searchTerm);
    },
    [debouncedSetQuery]
  );

  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);

  useEffect(() => {
    if (appUrl && options.length === 0 && !isLoading) {
      refetch();
    }
  }, [appUrl, options.length, isLoading, refetch]);

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    open,
    setOpen,
    query: inputValue,
    setQuery: handleSearchInputChange,
    options,
    isLoading,
    isError,
    error,
    loadProducts: loadNextPage,
    isFetchingNextPage,
    hasNextPage,
  };
};

export default useProductSelect;
